import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useWanNames } from "./WanNames";
import { fmtBytes, truncate } from "../utils/formatters";

// ── Helpers ──────────────────────────────────────────────────────────────────

const hashCode = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const STALE_SEC = 60;

export const getDeviceType = (device) => {
  const h = (device.hostname || "").toLowerCase();
  const m = (device.mac || "").toLowerCase();
  if (/iphone|android|phone|pixel|galaxy|oneplus|samsung/.test(h)) return "phone";
  if (/ipad|tablet/.test(h)) return "tablet";
  if (/macbook|laptop|thinkpad|dell|surface|notebook/.test(h)) return "laptop";
  if (/watch|fitbit|garmin/.test(h)) return "watch";
  if (/tv|roku|fire.?stick|chromecast|appletv|shield/.test(h)) return "tv";
  if (/echo|alexa|homepod|nest|sonos|speaker/.test(h)) return "speaker";
  if (/^eap\d|access.?point|unifi|omada|^ap[-_]|wifi|aruba|ruckus/.test(h)) return "ap";
  // MAC-based hinting
  if (/^(ac:de:48|00:17:88)/.test(m)) return "speaker"; // Philips Hue / Sonos
  return "device";
};

// ── Device icon paths (simple SVG) ──────────────────────────────────────────
// React.memo: type+size are primitive props that almost never change once
// a device is positioned, so the inner SVG tree can be reused across polls.

export const DeviceIcon = React.memo(function DeviceIcon({ type, size = 18 }) {
  const s = size;
  const common = {
    width: s, height: s, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor", strokeWidth: "2",
    strokeLinecap: "round", strokeLinejoin: "round",
  };

  switch (type) {
    case "phone":
      return (
        <svg {...common}>
          <rect x="5" y="2" width="14" height="20" rx="3" />
          <line x1="12" y1="18" x2="12" y2="18.01" />
        </svg>
      );
    case "tablet":
      return (
        <svg {...common}>
          <rect x="3" y="2" width="18" height="20" rx="3" />
          <line x1="12" y1="18" x2="12" y2="18.01" />
        </svg>
      );
    case "laptop":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="12" rx="2" />
          <line x1="2" y1="20" x2="22" y2="20" />
        </svg>
      );
    case "watch":
      return (
        <svg {...common}>
          <rect x="6" y="2" width="12" height="20" rx="4" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
    case "tv":
      return (
        <svg {...common}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
    case "speaker":
      return (
        <svg {...common}>
          <rect x="4" y="2" width="16" height="20" rx="3" />
          <circle cx="12" cy="14" r="4" />
          <circle cx="12" cy="6" r="1" />
        </svg>
      );
    case "ap":
      return (
        <svg {...common}>
          <path d="M5 12.55a11 11 0 0 1 14.08 0" />
          <path d="M1.42 9a16 16 0 0 1 21.16 0" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <line x1="6" y1="10" x2="6" y2="10.01" />
          <line x1="10" y1="10" x2="10" y2="10.01" />
        </svg>
      );
  }
});

// DeviceNode renders one device circle + icon + label. Memoised on
// primitive props so React.memo's shallow compare actually skips rerenders
// when the polled diag payload is unchanged. Without this, NetworkCanvas
// re-creates and re-renders ~50 SVG sub-trees every diag tick (5s).
//
// onEnter/onLeave/onClick are stable handlers (useCallback in parent)
// that take the device record/ip as argument; the inline arrows below
// don't bust memo because they live inside the memoised render.
const DeviceNode = React.memo(function DeviceNode({
  device,        // raw device record (referentially unstable per poll, so excluded from memo compare via isEqual below)
  ip,
  hostname,
  mac,
  type,
  x,
  y,
  isStale,
  isHovered,
  totalBytes,
  onEnter,
  onLeave,
  onClick,
}) {
  const nodeR = isHovered ? 22 : 20;
  const fillColor = isStale ? "#1e293b" : "#0f172a";
  const strokeColor = isStale ? "#475569" : isHovered ? "#22d3ee" : "#6366f1";
  const label = hostname && hostname !== "*" ? hostname : ip || "Unknown";
  const iconColor = isStale ? "#64748b" : isHovered ? "#22d3ee" : "#a5b4fc";
  const labelColor = isStale ? "#475569" : isHovered ? "#e2e8f0" : "#94a3b8";

  return (
    <g
      className="canvas-device-node"
      style={{
        cursor: "pointer",
        transform: "translate(0, 0)",
        transition: "transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease",
      }}
      onMouseEnter={() => onEnter(ip)}
      onMouseLeave={onLeave}
      onClick={() => onClick(device)}
    >
      <circle cx={x} cy={y} r={nodeR + 8} fill="transparent" />
      <circle
        cx={x}
        cy={y}
        r={nodeR}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={isHovered ? 2.5 : 1.5}
        filter={isHovered ? "url(#glowSm)" : undefined}
        style={{ transition: "all 0.25s ease" }}
      />
      <g
        transform={`translate(${x - 9}, ${y - 9})`}
        style={{ color: iconColor, transition: "color 0.25s ease" }}
      >
        <DeviceIcon type={type} size={18} />
      </g>
      <text
        x={x}
        y={y + nodeR + 14}
        textAnchor="middle"
        fill={labelColor}
        fontSize="9"
        fontWeight="600"
        style={{ transition: "fill 0.25s ease" }}
      >
        {label.length > 16 ? label.substring(0, 14) + "…" : label}
      </text>
      {!isStale && totalBytes > 0 && (
        <text
          x={x}
          y={y + nodeR + 24}
          textAnchor="middle"
          fill="#64748b"
          fontSize="7.5"
          fontWeight="500"
        >
          {fmtBytes(totalBytes)}
        </text>
      )}
    </g>
  );
}, (prev, next) => {
  // Custom compare: skip the `device` prop (referentially unstable across
  // polls). Re-render only when something visible to the user changed.
  return (
    prev.ip === next.ip &&
    prev.hostname === next.hostname &&
    prev.mac === next.mac &&
    prev.type === next.type &&
    prev.x === next.x &&
    prev.y === next.y &&
    prev.isStale === next.isStale &&
    prev.isHovered === next.isHovered &&
    prev.totalBytes === next.totalBytes &&
    prev.onEnter === next.onEnter &&
    prev.onLeave === next.onLeave &&
    prev.onClick === next.onClick
  );
});

// ── Main Component ───────────────────────────────────────────────────────────

export default function NetworkCanvas({
  devices,
  selectedNode,
  onDeviceClick,
  wans,
  systemHealth,
  tunnelStats,
  hideStale = false,
}) {
  const { wanName, wanColor } = useWanNames();
  const containerRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [dims, setDims] = useState({ w: 800, h: 520 });

  // Responsive sizing
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 100 && height > 100) {
        setDims({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cx = dims.w / 2;
  const cy = dims.h / 2;
  const baseRadius = Math.min(dims.w, dims.h) * 0.34;

  // Partition & sort devices by a stable key so indices never shuffle
  const { activeDevices, staleDevices } = useMemo(() => {
    const all = [...(devices || [])];
    // Sort by IP numerically for deterministic ordering
    all.sort((a, b) => {
      const ka = a.ip || a.mac || "";
      const kb = b.ip || b.mac || "";
      return ka.localeCompare(kb, undefined, { numeric: true });
    });
    const act = [];
    const st = [];
    for (const d of all) {
      if ((d.active_seconds ?? 0) > STALE_SEC) st.push(d);
      else act.push(d);
    }
    return { activeDevices: act, staleDevices: st };
  }, [devices]);

  const allDevices = useMemo(
    () => hideStale ? activeDevices : [...activeDevices, ...staleDevices],
    [activeDevices, staleDevices, hideStale]
  );

  // Assign each device a stable "slot" based on hash so its angle never changes
  // even if the total count shifts. We assign a fixed angle per device key.
  const positioned = useMemo(() => {
    const total = allDevices.length;
    if (total === 0) return [];
    return allDevices.map((d, i) => {
      const isStale = (d.active_seconds ?? 0) > STALE_SEC;
      const key = d.ip || d.mac || `dev-${i}`;
      const hash = hashCode(key);
      const angleOffset = (hash % 30 - 15) * (Math.PI / 180);
      const angle = ((i / total) * 2 * Math.PI - Math.PI / 2) + angleOffset;
      const rMul = isStale ? 1.3 : 0.85 + (hash % 20) / 100;
      const r = baseRadius * rMul;
      return {
        ...d,
        _key: key,
        _x: cx + r * Math.cos(angle),
        _y: cy + r * Math.sin(angle),
        _type: getDeviceType(d),
        _isStale: isStale,
        _totalBytes: (d.tx_bytes || 0) + (d.rx_bytes || 0),
      };
    });
  }, [allDevices, cx, cy, baseRadius]);

  // Cache previous positions per device key so we can smoothly interpolate
  const posCache = useRef({});
  useEffect(() => {
    for (const d of positioned) {
      posCache.current[d._key] = { x: d._x, y: d._y };
    }
  }, [positioned]);

  // Max traffic for line thickness scaling
  const maxTraffic = useMemo(
    () => Math.max(1, ...positioned.map((d) => d._totalBytes)),
    [positioned]
  );

  const handleDeviceClick = useCallback(
    (device) => {
      if (onDeviceClick) onDeviceClick(device);
    },
    [onDeviceClick]
  );
  const handleDeviceEnter = useCallback((ip) => setHovered(ip), []);
  const handleDeviceLeave = useCallback(() => setHovered(null), []);

  const wanList = useMemo(() => {
    if (!wans) return [];
    return Object.values(wans).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [wans]);

  return (
    <div className="canvas-container" ref={containerRef}>
      <svg
        className="canvas-svg"
        viewBox={`0 0 ${dims.w} ${dims.h}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Background gradient */}
          <radialGradient id="canvasBg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#131B2E" />
            <stop offset="100%" stopColor="#0B1120" />
          </radialGradient>

          {/* Edge node gradient */}
          <radialGradient id="edgeGrad" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#818CF8" />
            <stop offset="100%" stopColor="#4F46E5" />
          </radialGradient>

          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="glowSm" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Pulse animation for edge node */}
          <filter id="pulse" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background */}
        <rect width={dims.w} height={dims.h} fill="url(#canvasBg)" rx="16" />

        {/* Concentric rings */}
        {[0.4, 0.7, 1.0, 1.3].map((mul, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={baseRadius * mul}
            fill="none"
            stroke="rgba(99, 102, 241, 0.06)"
            strokeWidth="1"
            strokeDasharray="4 8"
          />
        ))}

        {/* Crosshair lines */}
        <line
          x1={cx - baseRadius * 1.4}
          y1={cy}
          x2={cx + baseRadius * 1.4}
          y2={cy}
          stroke="rgba(99, 102, 241, 0.04)"
          strokeWidth="1"
        />
        <line
          x1={cx}
          y1={cy - baseRadius * 1.4}
          x2={cx}
          y2={cy + baseRadius * 1.4}
          stroke="rgba(99, 102, 241, 0.04)"
          strokeWidth="1"
        />

        {/* Connection lines from edge to devices */}
        {positioned.map((d, i) => {
          const trafficRatio = d._totalBytes / maxTraffic;
          const opacity = d._isStale ? 0.08 : 0.12 + trafficRatio * 0.25;
          const width = d._isStale ? 1 : 1.5 + trafficRatio * 2;
          const isHov = hovered === d.ip;

          // Slight curve via quadratic bezier
          const mx = (cx + d._x) / 2;
          const my = (cy + d._y) / 2;
          const ddx = d._x - cx;
          const ddy = d._y - cy;
          const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
          const nx = -ddy / len;
          const ny = ddx / len;
          const curveOffset = (hashCode(d.ip || "") % 20) - 10;
          const qx = mx + nx * curveOffset;
          const qy = my + ny * curveOffset;

          return (
            <path
              key={`line-${d._key}`}
              d={`M ${cx} ${cy} Q ${qx} ${qy} ${d._x} ${d._y}`}
              fill="none"
              stroke={
                d._isStale
                  ? "#475569"
                  : isHov
                  ? "#22d3ee"
                  : "#6366f1"
              }
              strokeWidth={isHov ? width + 1 : width}
              opacity={isHov ? opacity + 0.2 : opacity}
              strokeLinecap="round"
              style={{ transition: "all 0.8s cubic-bezier(0.4,0,0.2,1)" }}
            />
          );
        })}

        {/* Edge node (center) */}
        <g className="canvas-edge-node">
          {/* Outer pulse ring */}
          <circle
            cx={cx}
            cy={cy}
            r={36}
            fill="none"
            stroke="#6366f1"
            strokeWidth="2"
            opacity="0.2"
            className="canvas-pulse-ring"
          />
          {/* Main circle */}
          <circle
            cx={cx}
            cy={cy}
            r={30}
            fill="url(#edgeGrad)"
            filter="url(#glow)"
            stroke="rgba(129, 140, 248, 0.4)"
            strokeWidth="2"
          />
          {/* Router icon */}
          <g transform={`translate(${cx - 10}, ${cy - 10})`}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <line x1="6" y1="10" x2="6" y2="10.01" />
              <line x1="10" y1="10" x2="10" y2="10.01" />
              <line x1="14" y1="10" x2="14" y2="10.01" />
            </svg>
          </g>
          {/* Label */}
          <text
            x={cx}
            y={cy + 46}
            textAnchor="middle"
            fill="#e2e8f0"
            fontSize="11"
            fontWeight="700"
            letterSpacing="0.05em"
          >
            {selectedNode || "EDGE"}
          </text>
        </g>

        {/* WAN indicators around edge */}
        {wanList.map((w, i) => {
          const wAngle = (Math.PI * 0.8) + (i / Math.max(1, wanList.length - 1)) * (Math.PI * 0.4);
          const wr = 56;
          const wx = cx + wr * Math.cos(wAngle);
          const wy = cy + wr * Math.sin(wAngle);
          const cable = wanColor(w.name);
          return (
            <g key={`wan-${w.name}`}>
              {/* The cable's colour rings the link's up/down dot. The pale
                  edge keeps a black cable visible on the dark canvas. */}
              {cable && (
                <circle cx={wx} cy={wy} r={11.5} fill="none" stroke={cable.paint}
                        strokeWidth="3" style={{ filter: "drop-shadow(0 0 1px rgba(226, 232, 240, 0.9))" }} />
              )}
              <circle
                cx={wx}
                cy={wy}
                r={8}
                fill={w.active === false ? "#ef4444" : "#22c55e"}
                opacity="0.7"
              />
              <text
                x={wx}
                y={wy + 18}
                textAnchor="middle"
                fill="#94a3b8"
                fontSize="8"
                fontWeight="600"
              >
                <title>{w.name}</title>
                {truncate(wanName(w.name), 14)}
              </text>
            </g>
          );
        })}

        {/* Device nodes */}
        {positioned.map((d) => (
          <DeviceNode
            key={`dev-${d._key}`}
            device={d}
            ip={d.ip}
            hostname={d.hostname}
            mac={d.mac}
            type={d._type}
            x={d._x}
            y={d._y}
            isStale={d._isStale}
            isHovered={hovered === d.ip}
            totalBytes={d._totalBytes}
            onEnter={handleDeviceEnter}
            onLeave={handleDeviceLeave}
            onClick={handleDeviceClick}
          />
        ))}

        {/* Hover tooltip */}
        {hovered && (() => {
          const d = positioned.find((p) => p.ip === hovered);
          if (!d) return null;
          const tw = 160;
          const th = 56;
          let tx = d._x + 28;
          let ty = d._y - th / 2;
          if (tx + tw > dims.w - 10) tx = d._x - tw - 28;
          if (ty < 10) ty = 10;
          if (ty + th > dims.h - 10) ty = dims.h - th - 10;

          return (
            <g className="canvas-tooltip">
              <rect
                x={tx}
                y={ty}
                width={tw}
                height={th}
                rx="8"
                fill="rgba(15, 23, 42, 0.92)"
                stroke="rgba(99, 102, 241, 0.3)"
                strokeWidth="1"
              />
              <text x={tx + 10} y={ty + 16} fill="#e2e8f0" fontSize="10" fontWeight="700">
                {d.hostname && d.hostname !== "*" ? d.hostname : d.ip}
              </text>
              <text x={tx + 10} y={ty + 30} fill="#94a3b8" fontSize="9">
                ↑ {fmtBytes(d.tx_bytes)} · ↓ {fmtBytes(d.rx_bytes)}
              </text>
              <text x={tx + 10} y={ty + 44} fill="#64748b" fontSize="8.5">
                {d.mac || "—"} · {d._type}
              </text>
            </g>
          );
        })()}

        {/* Empty state */}
        {positioned.length === 0 && (
          <g>
            <text
              x={cx}
              y={cy + 70}
              textAnchor="middle"
              fill="#475569"
              fontSize="13"
              fontWeight="500"
            >
              No devices connected
            </text>
            <text
              x={cx}
              y={cy + 86}
              textAnchor="middle"
              fill="#334155"
              fontSize="11"
            >
              Devices will appear here as they connect to the edge
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
